import { parsePhoneNumberFromString } from "libphonenumber-js";
import { PREVIEW_DESIGNS, industryForContact, industryLabel, industryPreviewPath, previewIndustryForContact } from "./industries.js";

export const ALLOWED_EMAIL_DOMAINS = ["marken-group.com", "markenbreit.com"];
export const PREVIEW_ORIGIN = "https://leadmagnet.marken-group.com";

export function normalizeEmail(value = "") {
  return value.trim().toLowerCase();
}

export function isAllowedEmail(value = "") {
  const email = normalizeEmail(value);
  const at = email.lastIndexOf("@");
  return at > 0 && ALLOWED_EMAIL_DOMAINS.includes(email.slice(at + 1));
}

export function normalizePhone(value = "", defaultCountry = "US") {
  const raw = value.trim();
  if (!raw) return { raw: "", e164: "", display: "", valid: true };
  const parsed = parsePhoneNumberFromString(raw, defaultCountry || "US");
  if (!parsed?.isValid()) return { raw, e164: "", display: raw, valid: false };
  return { raw, e164: parsed.number, display: parsed.formatInternational(), valid: true };
}

export function extractGooglePlaceId(value = "") {
  if (!value.trim()) return "";
  try {
    const url = new URL(value.trim());
    const direct = url.searchParams.get("query_place_id") || url.searchParams.get("place_id");
    if (direct) return direct;
    const query = url.searchParams.get("q") || "";
    const queryMatch = query.match(/^place_id:([\w-]+)$/i);
    if (queryMatch) return queryMatch[1];
    const decoded = decodeURIComponent(url.href);
    return decoded.match(/!1s(ChI[\w-]+)/)?.[1] || "";
  } catch {
    return "";
  }
}

export function isGoogleMapsUrl(value = "") {
  if (!value.trim()) return false;
  try {
    const url = new URL(value.trim());
    if (url.protocol !== "https:") return false;
    const hostname = url.hostname.toLowerCase();
    if (hostname === "maps.google.com" || hostname === "maps.app.goo.gl") return true;
    return (hostname === "google.com" || hostname === "www.google.com")
      && (url.pathname === "/maps" || url.pathname.startsWith("/maps/"));
  } catch {
    return false;
  }
}

export function buildPreviewUrl(contactId) {
  return `${PREVIEW_ORIGIN}/?preview=${encodeURIComponent(contactId)}`;
}

const previewDesignLabels = {
  conversion: "Design 1 · Anfragen",
  premium: "Design 2 · Premium",
  trust: "Design 3 · Vertrauen"
};

export function buildDesignPreviewUrls(contact = {}) {
  if (contact.previewEnabled === false || !contact.id) return [];
  const industry = previewIndustryForContact(contact);
  if (!industry) return [];
  return PREVIEW_DESIGNS.map((design) => ({
    design,
    label: previewDesignLabels[design],
    industry,
    url: `${PREVIEW_ORIGIN}${industryPreviewPath(industry, design)}?preview=${encodeURIComponent(contact.id)}`
  }));
}

export function googleRatingLabel(rating) {
  if (!Number.isFinite(rating)) return "";
  if (rating >= 4.5) return "Sehr gut";
  if (rating >= 4) return "Gut";
  if (rating >= 3) return "Durchschnittlich";
  return "Schwach";
}

export function googleRatingTone(rating) {
  if (!Number.isFinite(rating)) return "unknown";
  if (rating >= 4) return "good";
  if (rating >= 3) return "average";
  return "weak";
}

export function renderEmailTemplate(value = "", contact = {}, senderName = "") {
  const replacements = {
    contactName: contact.contactName || "there",
    companyName: contact.companyName || "your company",
    city: contact.address?.city || "your area",
    previewUrl: contact.id ? buildPreviewUrl(contact.id) : PREVIEW_ORIGIN,
    senderName: senderName || "MarkenGroup"
  };
  return value.replace(/{{\s*(contactName|companyName|city|previewUrl|senderName)\s*}}/g, (_, key) => replacements[key]);
}

export function buildMailtoLink(contact, template, senderName, senderEmail = "") {
  const subject = renderEmailTemplate(template.subject, contact, senderName);
  const body = renderEmailTemplate(template.body, contact, senderName);
  const parameters = [
    senderEmail.trim() ? `from=${encodeURIComponent(senderEmail.trim())}` : "",
    template.cc?.trim() ? `cc=${encodeURIComponent(template.cc.trim())}` : "",
    template.bcc?.trim() ? `bcc=${encodeURIComponent(template.bcc.trim())}` : "",
    `subject=${encodeURIComponent(subject)}`,
    `body=${encodeURIComponent(body)}`
  ].filter(Boolean).join("&");
  return `mailto:${encodeURIComponent(contact.email || "")}?${parameters}`;
}

export function contactMatches(contact, rawSearch) {
  const search = rawSearch.trim().toLowerCase();
  if (!search) return true;
  const digits = search.replace(/\D/g, "");
  const values = [
    contact.contactName,
    contact.companyName,
    contact.email,
    contact.website,
    contact.address?.city,
    contact.address?.formatted,
    contact.phone?.display,
    contact.phone?.e164,
    industryForContact(contact),
    industryLabel(industryForContact(contact))
  ].filter(Boolean).map((value) => String(value).toLowerCase());
  return values.some((value) => value.includes(search)) || (digits.length >= 3 && values.some((value) => value.replace(/\D/g, "").includes(digits)));
}

export function contactMatchesStatus(contact, status = "all") {
  return status === "all" || contact.status === status;
}

export function websiteQuality(website = "") {
  const value = String(website).trim();
  if (!value) return "poor";
  try {
    return new URL(value).protocol === "https:" ? "secure" : "poor";
  } catch {
    return "poor";
  }
}

export function contactMatchesWebsiteQuality(contact, quality = "all") {
  return quality === "all" || websiteQuality(contact.website) === quality;
}

export function groupContactsByStatus(contacts, statusOrder) {
  return statusOrder
    .map((status) => ({ status, contacts: contacts.filter((contact) => contact.status === status) }))
    .filter((group) => group.contacts.length > 0);
}

export function groupContactsByValue(contacts, valueForContact, emptyLabel = "Ohne Angabe") {
  const groups = new Map();
  contacts.forEach((contact) => {
    const label = String(valueForContact(contact) || "").trim() || emptyLabel;
    const key = label.toLocaleLowerCase("de-DE");
    if (!groups.has(key)) groups.set(key, { key, label, contacts: [] });
    groups.get(key).contacts.push(contact);
  });
  return [...groups.values()].sort((left, right) => left.label.localeCompare(right.label, "de-DE"));
}

export function formatTimestamp(value) {
  if (!Number.isFinite(value)) return "Gerade eben";
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

export function displayNameForUser(user) {
  if (user?.displayName?.trim()) return user.displayName.trim();
  return user?.email?.split("@")[0] || "MarkenGroup Team";
}
